import {
  ILineDataInVerticalStackedBarChart,
  IVerticalStackedChartProps,
  IVSChartDataPoint,
  VerticalStackedBarChart,
} from '@fluentui/react-charting';
import AppContext from 'App/AppContext';
import { IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import TenantPicker, { PickerTenant, TenantPickerMode } from 'components/Pickers/TenantPicker';
import { getRoleColor, GetRoleTranslation } from 'globalFunctions';
import { globalStackTokensGapMedium } from 'globalStyles';
import { Widget74ResultDTO } from 'models/dto/widgets/widget74DTO';
import { isPartnerWidgetType, WidgetSizes, WidgetTenantConfig, WidgetWidth } from 'models/widget';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetWidgetDataTenantUserStats } from 'services/Api/dashboardService';
import { appRoles } from 'services/Auth/appRoles';
import { apiRequest } from 'services/Auth/authConfig';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import { fromApiDateTimeToDateOptional, toLocaleDateShort } from 'utils/datetime';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';
import {
  getConfiguredTenantIds,
  getTenantWidgetTenantById,
  getTenantWidgetTenantId,
  getTenantWidgetTenantName,
  sortTenantWidgetTenantsByName,
  TenantWidgetTenant,
  WithTenantId,
  withTenantId,
} from './tenantWidgetData';
import { useTenantDrillDown } from './useTenantDrillDown';

export class WidgetTenantUserStatsConfig extends WidgetTenantConfig {
  constructor() {
    super();
  }

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.loadTenant(newRawConfig);
      } catch {
        //ignore
      }
    }
  }

  clone(): WidgetTenantUserStatsConfig {
    const newConfig = this.cloneTenant() as WidgetTenantUserStatsConfig;

    return newConfig;
  }
}

interface IWidgetTenantUserStatsProps extends IWidgetRendererProps {}

const WidgetTenantUserStats = (props: IWidgetTenantUserStatsProps) => {
  const { t } = useTranslation(['translation', 'widgets']);
  const appContext = useContext(AppContext);
  const [data, setData] = useState<Widget74ResultDTO[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tenants, setTenants] = useState<TenantWidgetTenant[]>([]);
  const [tenantsToShow, setTenantsToShow] = useState<TenantWidgetTenant[]>([]);
  const [isGlobalAdmin] = useState<boolean>(hasUserFeature(appContext, FeatureTypes.TenantGlobalAdmin));

  useEffect(() => {
    const loadData = async () => {
      try {
        if (isLoading) return;
        setIsLoading(true);

        await appContext.globalDataCache.tags.getItems();
        const config = loadConfig();

        const tenants: TenantWidgetTenant[] = isPartnerWidgetType(props.widget.widgetType)
          ? props.sharedData?.partnerCustomers ?? []
          : props.sharedData?.userOrgUnits ?? [];
        setTenants(tenants);
        const tenantIds = getConfiguredTenantIds(config.tenants, tenants);
        if (tenantIds.length === 0) {
          setData([]);
          setTenantsToShow([]);

          return;
        }

        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const data = await apiGetWidgetDataTenantUserStats(tenantIds, accessToken, props.abortSignal);

        setData(data);

        const tenantsToShow = tenants.filter((tenant) =>
          data.some((s) => s.tenantId === getTenantWidgetTenantId(tenant)),
        );
        sortTenantWidgetTenantsByName(tenantsToShow);
        setTenantsToShow(tenantsToShow);
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget, props.sharedData?.partnerCustomers, props.sharedData?.userOrgUnits]);

  const loadConfig = (): WidgetTenantUserStatsConfig => {
    const newConfig = new WidgetTenantUserStatsConfig();
    newConfig.load(props.widget.widgetConfig);

    return newConfig;
  };

  const getData = (): IVerticalStackedChartProps[] => {
    if (data.length === 0 || tenantsToShow.length === 0) return [];

    const graphData: WithTenantId<IVerticalStackedChartProps>[] = tenantsToShow.map((tenant) => {
      const tenantId = getTenantWidgetTenantId(tenant);
      const tenantData = data.find((s) => s.tenantId === tenantId);
      const tenantName = getTenantWidgetTenantName(tenant);
      const dataPoints: IVSChartDataPoint[] = [];
      const lastLoginDate = fromApiDateTimeToDateOptional(tenantData?.lastLogin);

      if (tenantData) {
        if (isGlobalAdmin) {
          dataPoints.push({
            legend: GetRoleTranslation(appRoles.TenantGlobalAdmin, t),
            data: tenantData.roleCount[appRoles.TenantGlobalAdmin] ?? 0,
            color: getRoleColor(appRoles.TenantGlobalAdmin),
            xAxisCalloutData: tenantName,
          });

          dataPoints.push({
            legend: GetRoleTranslation(appRoles.TenantISOAdmin, t),
            data: tenantData.roleCount[appRoles.TenantISOAdmin] ?? 0,
            color: getRoleColor(appRoles.TenantISOAdmin),
            xAxisCalloutData: tenantName,
          });

          dataPoints.push({
            legend: GetRoleTranslation(appRoles.TenantAdmin, t),
            data: tenantData.roleCount[appRoles.TenantAdmin] ?? 0,
            color: getRoleColor(appRoles.TenantAdmin),
            xAxisCalloutData: tenantName,
          });
        }

        dataPoints.push({
          legend: GetRoleTranslation(appRoles.OrgAdmin, t),
          data: tenantData.roleCount[appRoles.OrgAdmin] ?? 0,
          color: getRoleColor(appRoles.OrgAdmin),
          xAxisCalloutData: tenantName,
        });

        dataPoints.push({
          legend: GetRoleTranslation(appRoles.Admin, t),
          data: tenantData.roleCount[appRoles.Admin] ?? 0,
          color: getRoleColor(appRoles.Admin),
          xAxisCalloutData: tenantName,
        });

        dataPoints.push({
          legend: GetRoleTranslation(appRoles.Manager, t),
          data: tenantData.roleCount[appRoles.Manager] ?? 0,
          color: getRoleColor(appRoles.Manager),
          xAxisCalloutData: tenantName,
        });

        dataPoints.push({
          legend: GetRoleTranslation(appRoles.Consultant, t),
          data: tenantData.roleCount[appRoles.Consultant] ?? 0,
          color: getRoleColor(appRoles.Consultant),
          xAxisCalloutData: tenantName,
        });

        dataPoints.push({
          legend: GetRoleTranslation(appRoles.LicenseManager, t),
          data: tenantData.roleCount[appRoles.LicenseManager] ?? 0,
          color: getRoleColor(appRoles.LicenseManager),
          xAxisCalloutData: tenantName,
        });

        dataPoints.push({
          legend: GetRoleTranslation(appRoles.User, t),
          data: tenantData.roleCount[appRoles.User] ?? 0,
          color: getRoleColor(appRoles.User),
          xAxisCalloutData: tenantName,
        });

        dataPoints.push({
          legend: t('widgets:WidgetTenantUserStatsOrgUnit.ServicePrincipals'),
          data: tenantData.servicePrincipalCount,
          color: '#7c75e7',
          xAxisCalloutData: tenantName,
        });

        let inactiveOwnersStr = '';
        if (tenantData.inactiveOwners.length > 0) {
          inactiveOwnersStr = ` (${tenantData.inactiveOwners.map((u) => u.name).join(', ')})`;
        }

        dataPoints.push({
          legend: t('widgets:WidgetTenantUserStatsOrgUnit.InactiveOwners'),
          data: tenantData.inactiveOwners.length,
          color: '#d41a1a',
          xAxisCalloutData: tenantName,
          yAxisCalloutData: `${tenantData.inactiveOwners.length}${inactiveOwnersStr}`,
        });

        dataPoints.push({
          legend: t('widgets:WidgetTenantUserStatsOrgUnit.InactiveUsers'),
          data: tenantData.inactiveUserCount,
          color: '#d3d3d3',
          xAxisCalloutData: tenantName,
        });
      }

      const lineData: ILineDataInVerticalStackedBarChart = {
        legend: t('widgets:WidgetTenantUserStatsOrgUnit.MonthlyActiveUsers'),
        y: tenantData?.monthlyActiveUsers ?? 0,
        color: '#dd91e4',
        useSecondaryYScale: true,
        yAxisCalloutData: `${tenantData?.monthlyActiveUsers ?? 0} (${t(
          'widgets:WidgetTenantUserStatsOrgUnit.LastLogin',
        )}: ${lastLoginDate ? toLocaleDateShort(lastLoginDate) : '-'})`,
      };

      return {
        chartData: withTenantId(dataPoints, tenantId),
        lineData: [lineData],
        xAxisPoint: tenantName,
        tenantId,
      } as WithTenantId<IVerticalStackedChartProps>;
    });

    return graphData;
  };

  const drillDown = useTenantDrillDown();

  const onClick = (data: IVerticalStackedChartProps) => {
    const tenant = getTenantWidgetTenantById(tenants, (data as WithTenantId<IVerticalStackedChartProps>).tenantId);
    if (!tenant) return;
    const url = '/admin/users';
    drillDown(tenant, url, isPartnerWidgetType(props.widget.widgetType));
  };

  //
  // Main render
  //
  if (!data || isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (!isLoading && (data.length === 0 || tenantsToShow.length === 0)) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('translation:General.Notifications.NoItemsFilter')}</IPText>
      </IPStack>
    );
  }

  const shouldRotateXAxisLabels = data.length > 10 && props.widget.widgetSize !== WidgetSizes.Large;

  return (
    <IPStack verticalFill>
      <IPStack.Item grow>
        <VerticalStackedBarChart
          data={getData()}
          height={250}
          width={WidgetWidth[props.widget.widgetSize] - 80}
          wrapXAxisLables={!shouldRotateXAxisLabels}
          rotateXAxisLables={shouldRotateXAxisLabels}
          barWidth={'auto'}
          legendProps={{
            centerLegends: true,
          }}
          onBarClick={(ev, data) => onClick(data as IVerticalStackedChartProps)}
          secondaryYScaleOptions={{}}
          barMinimumHeight={3}
          margins={{ top: 30, right: 40, bottom: 30, left: 40 }}
        />
      </IPStack.Item>
    </IPStack>
  );
};

export default WidgetTenantUserStats;

//
// Config
//
interface IWidgetConfigTenantUserStatsProps extends IWidgetConfigRendererProps {}

export const WidgetConfigTenantUserStats = (props: IWidgetConfigTenantUserStatsProps) => {
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedTenants, setSelectedTenants] = useState<string[]>([]);
  const [currentWidgetId, setCurrentWidgetId] = useState<number>(-1);

  useEffect(() => {
    if (currentWidgetId === -1 || currentWidgetId !== props.widget.widgetId) {
      setCurrentWidgetId(props.widget.widgetId);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadConfig = (): WidgetTenantUserStatsConfig => {
    let newRawConfig = new WidgetTenantUserStatsConfig();
    if (props.widget.widgetConfig) {
      newRawConfig = JSON.parse(props.widget.widgetConfig);
    }

    return newRawConfig;
  };

  const updateTenants = (items: PickerTenant[]) => {
    const tenants = items.map((i) => i.tenantId);
    setSelectedTenants(tenants);
    const config = getConfig(tenants);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      const config = loadConfig();
      setSelectedTenants(config.tenants);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getConfig = (items: string[]): WidgetTenantUserStatsConfig => {
    const config = new WidgetTenantUserStatsConfig();
    config.tenants = items;

    return config;
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <TenantPicker
          mode={isPartnerWidgetType(props.widget.widgetType) ? TenantPickerMode.Partner : TenantPickerMode.User}
          onSelect={updateTenants}
          selectedTenants={selectedTenants}
          emptySelectionMeansAll
        />
      </IPStack.Item>
    </IPStack>
  );
};
